import { memo, useEffect, useRef, useState } from 'react';
import { History as HistoryIcon, Pin, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { HistoryEntry } from '../lib/canvas-history';

/** Placement mirrors PropertiesPanel / LayersPanel — when both
 *  side panels are mounted in landscape the History panel docks
 *  between them on the right column; portrait stacks all three
 *  vertically beneath the canvas. */
export type HistoryPanelPlacement = 'right' | 'bottom';

interface HistoryPanelProps {
  /** Whether the panel is expanded. When `false` the panel
   *  collapses to a single-line "Show history" affordance so the
   *  user can re-open it without leaving the right-column dock. */
  isOpen: boolean;
  /** Right (landscape) or bottom (portrait) — controls the border
   *  + max-height styling so the panel matches PropertiesPanel
   *  and LayersPanel in either layout. */
  placement: HistoryPanelPlacement;
  /** Toggle the expanded / collapsed state. Wired to the
   *  `view.toggle-history-panel` command + the toolbar History
   *  icon button. */
  onToggle: () => void;
  /** Ordered list of history entries, oldest first (matches
   *  Photoshop's panel layout). Each render of EditorPage re-reads
   *  from the engine; the engine's getEntries cache yields a
   *  stable reference between mutations so memoized rows skip
   *  re-renders when only their unrelated siblings change. */
  entries: readonly HistoryEntry[];
  /** Id of the entry currently applied to the canvas. The row
   *  matching this id gets the "current" highlight. Null when the
   *  stack is empty. */
  currentEntryId: string | null;
  /** Click-to-jump callback. The engine no-ops cleanly when the
   *  id is unknown or already current; the panel doesn't need to
   *  pre-filter. */
  onJumpTo: (entryId: string) => void;
  /** "New Snapshot" affordance — same path the Ctrl+Shift+H
   *  shortcut runs. */
  onManualSnapshot: () => void;
  /** "Clear History" affordance — drops every entry (including
   *  manual snapshots). The panel gates this behind a confirm
   *  dialog so an accidental click can't destroy a session's
   *  history; the parent's callback runs only on confirm. */
  onClearHistory: () => void;
}

/** Photoshop-style history panel.
 *
 *  Renders inline in the right column between Properties and
 *  Layers (matching the LayersPanel collapsible pattern), so
 *  toggling on or off shares the column height with its siblings
 *  rather than overlaying the canvas. Future work (Tier 3 Group C
 *  Phase 4) will add lazy thumbnails per row via an
 *  IntersectionObserver + cached canvas-to-image render; for now
 *  the panel is label + manual-pin marker + click-to-jump only,
 *  which is the full functional contract minus the visual sugar. */
export function HistoryPanel({
  isOpen,
  placement,
  onToggle,
  entries,
  currentEntryId,
  onJumpTo,
  onManualSnapshot,
  onClearHistory,
}: HistoryPanelProps) {
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const isRight = placement === 'right';
  // Auto-scroll the current row into view on every change of
  // `currentEntryId`. Without this the panel can scroll the user
  // somewhere else after a long undo run. Scroll within the panel
  // only — never touches `window.scrollTo`. `block: 'nearest'`
  // skips the scroll when the row is already visible, which is
  // the common case during keyboard tab-nav.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isOpen || !currentEntryId) return;
    const el = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-history-entry="${CSS.escape(currentEntryId)}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, currentEntryId]);

  // Up / Down arrow navigation through the entries. Photoshop's
  // history panel supports the same — keyboard-only users would
  // otherwise need one Tab per row, which is unworkable past a
  // dozen entries. Up = older (toward index 0), Down = newer
  // (toward the head). Focus moves to the newly-current row so
  // subsequent arrow presses continue from there.
  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (entries.length === 0) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const currentIdx = currentEntryId
      ? entries.findIndex((x) => x.id === currentEntryId)
      : entries.length - 1;
    let targetIdx: number | null = null;
    if (e.key === 'ArrowDown' && currentIdx < entries.length - 1) {
      targetIdx = currentIdx + 1;
    } else if (e.key === 'ArrowUp' && currentIdx > 0) {
      targetIdx = currentIdx - 1;
    }
    if (targetIdx === null) return;
    e.preventDefault();
    const targetId = entries[targetIdx].id;
    onJumpTo(targetId);
    // Buttons stay mounted across cursor changes — only `aria-current`
    // flips — so the target's DOM node already exists and can be
    // focused synchronously. RAF wrap defers focus past any
    // React commit triggered by onJumpTo so we don't focus a node
    // about to be replaced.
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLButtonElement>(
        `[data-history-entry="${CSS.escape(targetId)}"]`,
      );
      el?.focus();
    });
  };

  // Collapsed state — single-line affordance matching the
  // LayersPanel pattern. Placed AFTER every hook above (per the
  // rules-of-hooks comment in LayersPanel) so the hook count
  // stays constant across `isOpen` flips.
  if (!isOpen) {
    return (
      <button
        type="button"
        className={
          isRight
            ? 'h-10 w-full bg-background border-l border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground shrink-0'
            : 'w-full h-10 bg-background border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground'
        }
        onClick={onToggle}
        title="Show history"
      >
        <span className="text-xs font-medium">History</span>
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  const containerClass = isRight
    ? 'w-full bg-background border-l border-t border-border flex flex-col shrink-0 max-h-[40vh]'
    : 'w-full bg-background border-t border-border flex flex-col max-h-[25vh]';

  return (
    <aside role="region" aria-label="History panel" className={containerClass}>
      <header className="flex items-center justify-between px-3 h-10 border-b border-border shrink-0">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
          History
        </h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onManualSnapshot}
            title="New snapshot (Ctrl+Shift+H)"
            aria-label="Create new snapshot"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none"
            onClick={() => setConfirmClearOpen(true)}
            disabled={entries.length === 0}
            title="Clear all history"
            aria-label="Clear all history"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-1"
            onClick={onToggle}
          >
            Hide
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto py-1 min-h-0"
        role="list"
        aria-label="History entries"
        onKeyDown={handleListKeyDown}
      >
        {entries.length === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground text-center">
            No history yet. Make an edit to start.
          </p>
        ) : (
          entries.map((entry) => (
            <HistoryRow
              key={entry.id}
              entry={entry}
              isCurrent={entry.id === currentEntryId}
              onJumpTo={onJumpTo}
            />
          ))
        )}
      </div>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all history?</AlertDialogTitle>
            <AlertDialogDescription>
              This drops every entry, including pinned manual snapshots. The current canvas state is
              preserved — undo will no longer be available afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onClearHistory();
                setConfirmClearOpen(false);
              }}
            >
              Clear history
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

interface HistoryRowProps {
  entry: HistoryEntry;
  isCurrent: boolean;
  onJumpTo: (entryId: string) => void;
}

/** Memoized row — at 200 entries × every undo/redo, the panel
 *  re-renders the full list. Without this memo, every row re-runs
 *  its full body on every parent render even when neither `entry`
 *  nor `isCurrent` changed. The entry reference is stable thanks
 *  to the engine's getEntries cache. */
const HistoryRow = memo(function HistoryRow({ entry, isCurrent, onJumpTo }: HistoryRowProps) {
  return (
    <div role="listitem">
      <button
        type="button"
        aria-current={isCurrent ? 'true' : undefined}
        data-history-entry={entry.id}
        onClick={() => onJumpTo(entry.id)}
        className={[
          'w-full text-left px-3 py-1.5 text-xs flex items-center gap-2',
          isCurrent ? 'bg-accent text-foreground' : 'hover:bg-accent/50 text-muted-foreground',
        ].join(' ')}
        title={formatTooltip(entry)}
      >
        {entry.isManual ? (
          <>
            <Pin className="h-3 w-3 flex-shrink-0 text-blue-500" aria-hidden />
            <span className="sr-only">Manual snapshot:</span>
          </>
        ) : (
          <span className="h-3 w-3 flex-shrink-0" aria-hidden />
        )}
        <span className={`truncate ${entry.isManual ? 'font-medium' : ''}`}>{entry.label}</span>
      </button>
    </div>
  );
});

function formatTooltip(entry: HistoryEntry): string {
  const time = new Date(entry.timestamp).toLocaleTimeString();
  const parts = [entry.label, time];
  if (entry.commandId) parts.push(`(${entry.commandId})`);
  if (entry.isManual) parts.push('— manual snapshot');
  return parts.join(' · ');
}
